import {createApi} from "@reduxjs/toolkit/query/react"
import {IBasicResponse} from "../../types/api"
import baseQuery from "../../utils/apiIIKOConfig"
import {
    IDeletePromotionParams,
    IBackfillSegmentParams,
    ICreatePromotionResponse,
    ICreateSegmentResponse,
    IGetSegmentParams,
    IDeleteSegmentParams,
    ISegmentDetailsResponse,
    ISegmentListResponse,
    ISegmentRuleTypesResponse,
    ISegmentSavePayload,
    IUpdateSegmentParams,
    IPromotionListParams,
    IPromotionListResponse,
    IPromotionSavePayload,
    IUpdatePromotionParams
} from "./PromotionApiTypes"

export const promotionApi = createApi({
    reducerPath: "promotionApi",
    baseQuery,
    tagTypes: ["promotions", "segments", "segmentRuleTypes"],
    endpoints: (build) => ({
        getPromotions: build.query<IPromotionListResponse, IPromotionListParams>({
            query: ({lang, ...body}) => ({
                url: "/v1/promotions/list",
                method: "POST",
                headers: {
                    "Accept-Language": lang,
                    "X-Time-Zone": "Asia/Tashkent",
                },
                body
            }),
            providesTags: ["promotions"]
        }),
        createPromotion: build.mutation<IBasicResponse<ICreatePromotionResponse>, IPromotionSavePayload>({
            query: ({lang, ...body}) => ({
                url: "/v1/promotions",
                method: "POST",
                headers: {
                    "Accept-Language": lang,
                    "X-Time-Zone": "Asia/Tashkent",
                },
                body
            }),
            invalidatesTags: ["promotions"]
        }),
        updatePromotion: build.mutation<void, IUpdatePromotionParams>({
            query: ({lang, id, ...body}) => ({
                url: `/v1/promotions/${id}`,
                method: "PUT",
                headers: {
                    "Accept-Language": lang,
                    "X-Time-Zone": "Asia/Tashkent",
                },
                body
            }),
            invalidatesTags: ["promotions"]
        }),
        deletePromotion: build.mutation<void, IDeletePromotionParams>({
            query: ({lang, id}) => ({
                url: `/v1/promotions/${id}`,
                method: "DELETE",
                headers: {
                    "Accept-Language": lang,
                    "X-Time-Zone": "Asia/Tashkent"
                }
            }),
            invalidatesTags: ["promotions"]
        }),
        getSegmentsRuleTypes: build.query<ISegmentRuleTypesResponse, {lang: string}>({
            query: ({lang}) => ({
                url: "/v1/segments/rule-types",
                method: "GET",
                headers: {
                    "Accept-Language": lang,
                    "X-Time-Zone": "Asia/Tashkent"
                }
            }),
            providesTags: ["segmentRuleTypes"]
        }),
        getSegments: build.query<ISegmentListResponse, {lang: string}>({
            query: ({lang}) => ({
                url: "/v1/segments",
                method: "GET",
                headers: {
                    "Accept-Language": lang,
                    "X-Time-Zone": "Asia/Tashkent"
                }
            }),
            providesTags: ["segments"]
        }),
        getSegmentById: build.query<ISegmentDetailsResponse, IGetSegmentParams>({
            query: ({lang, id}) => ({
                url: `/v1/segments/${id}`,
                method: "GET",
                headers: {
                    "Accept-Language": lang,
                    "X-Time-Zone": "Asia/Tashkent"
                }
            }),
            providesTags: (_result, _error, params) => [{type: "segments", id: params.id}]
        }),
        createSegment: build.mutation<IBasicResponse<ICreateSegmentResponse>, ISegmentSavePayload>({
            query: ({lang, ...body}) => ({
                url: "/v1/segments",
                method: "POST",
                headers: {
                    "Accept-Language": lang,
                    "X-Time-Zone": "Asia/Tashkent",
                },
                body
            }),
            invalidatesTags: ["segments"]
        }),
        updateSegment: build.mutation<void, IUpdateSegmentParams>({
            query: ({lang, id, ...body}) => ({
                url: `/v1/segments/${id}`,
                method: "PUT",
                headers: {
                    "Accept-Language": lang,
                    "X-Time-Zone": "Asia/Tashkent",
                },
                body
            }),
            invalidatesTags: ["segments"]
        }),
        deleteSegment: build.mutation<void, IDeleteSegmentParams>({
            query: ({lang, id}) => ({
                url: `/v1/segments/${id}`,
                method: "DELETE",
                headers: {
                    "Accept-Language": lang,
                    "X-Time-Zone": "Asia/Tashkent"
                }
            }),
            invalidatesTags: ["segments"]
        }),
        backfillSegment: build.mutation<void, IBackfillSegmentParams>({
            query: ({lang, id}) => ({
                url: `/v1/segments/${id}/backfill`,
                method: "POST",
                headers: {
                    "Accept-Language": lang,
                    "X-Time-Zone": "Asia/Tashkent"
                }
            }),
            invalidatesTags: ["segments"]
        })
    })
})

export const {
    useGetPromotionsQuery,
    useGetSegmentsRuleTypesQuery,
    useGetSegmentsQuery,
    useLazyGetSegmentByIdQuery,
    useCreateSegmentMutation,
    useUpdateSegmentMutation,
    useDeleteSegmentMutation,
    useBackfillSegmentMutation,
    useCreatePromotionMutation,
    useUpdatePromotionMutation,
    useDeletePromotionMutation
} = promotionApi
